import { useEffect } from 'react';
import Map from 'ol/Map';
import { Port } from './AISMapContext';
import { useCoverage } from './Coverage/CoverageContext';
import { SourceIdentifier } from '@ais-ui/api-spec/coverage';
import { useSelectionState } from '../../components/SelectionStateProvider';
import { useGPSLost } from './GpsLost/GPSLostContext';

interface ListenForLayerClicksProps {
  map: Map;
}

export const ListenForLayerClicks = ({ map }: ListenForLayerClicksProps) => {
  const { setSelectedPorts } = useSelectionState();
  const { onSourceIdentifierSelect } = useCoverage();
  const {
    setSelectedGPSLostGridCell,
    setClickCoordinate: setGPSLostClickCoordinate,
  } = useGPSLost();

  useEffect(() => {
    const handleLayerClick = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { properties, isCtrlClick, coordinate, position } =
        customEvent.detail;

      if (properties?.type === 'port') {
        // We add the port to the list of selected ports if CTRL is clicked
        if (isCtrlClick) {
          setSelectedPorts((prevPorts) => {
            const selectedPort: Port = {
              id: properties.id,
              name: properties.name,
              country: properties.country,
              locode: properties.locode,
              geometry: properties.geometry,
            };

            return [...prevPorts, selectedPort];
          });
        } else {
          setSelectedPorts([
            {
              id: properties.id,
              name: properties.name,
              country: properties.country,
              locode: properties.locode,
              geometry: properties.geometry,
            },
          ]);
        }
      } else if (properties?.type === 'source-identifier') {
        // Use the same onSourceIdentifierSelect function as the UI list
        // This ensures consistent behavior and proper coverage validation
        const sourceIdentifier: SourceIdentifier = {
          id: parseInt(properties.id),
          countryMid: properties.countryMid,
          pos: properties.pos,
          regionId: properties.regionId,
          pssMmsi: properties.pssMmsi,
          sourceTag: properties.sourceTag,
          covEnabled: properties.covEnabled,
          createdTimeUtc: properties.createdTimeUtc,
          uuid: properties.uuid,
        };

        // Use the same selection logic as the UI list
        onSourceIdentifierSelect(sourceIdentifier);
      } else if (properties?.type === 'gps-lost') {
        setSelectedGPSLostGridCell({
          id: properties.id,
          configurationId: properties.configurationId,
          instanceId: properties.instanceId,
          mmsis: properties.mmsi_list,
          index: properties.index,
          gridLayerId: properties.gridLayerId,
        });
        setGPSLostClickCoordinate(coordinate);
      }
    };

    const mapElement = map.getTargetElement(); // Get the DOM element associated with the map

    if (mapElement) {
      mapElement.addEventListener('layer-click', handleLayerClick);
    }

    return () => {
      if (mapElement) {
        mapElement.removeEventListener('layer-click', handleLayerClick);
      }
    };
  }, [
    map,
    onSourceIdentifierSelect,
    setSelectedPorts,
    setSelectedGPSLostGridCell,
    setGPSLostClickCoordinate,
  ]);

  return null;
};
